import { clsx } from 'clsx';
import { Message } from 'game/components/Message';
import { FILTER_GLOW_ID } from 'game/defs/FilterGlow';
import { FILTER_GLOW_SUBTLE_ID } from 'game/defs/FilterGlowSubtle';
import { PATTERN_LINES_ID } from 'game/defs/PatternLines';
import { createStyles } from 'game/lib/createStyles';
import { InventoryModule, InventoryOperation, InventorySelection, ModuleType } from 'game/state/BotState';
import { ModuleComponentProps } from './ModuleProps';
import { HerringIcon } from './icons/HerringIcon';
import { WaterIcon } from './icons/WaterIcon';
import { moduleStyles } from './moduleStyles';

const messages: Record<InventorySelection, string> = {
  [InventorySelection.RedHerring]: 'Red herring',
  [InventorySelection.Water]: 'Water',
};

const operations = Object.values(InventoryOperation);

const advanceOperation = (module: InventoryModule, howMany: number) => {
  const i = operations.indexOf(module.config.operation);
  const nextChoice = operations[(i + howMany + operations.length) % operations.length] || null;
  module.config.operation = nextChoice;
};

const renderItem = (selection: InventorySelection): JSX.Element => {
  switch (selection) {
    case InventorySelection.Water: return <WaterIcon />;
    case InventorySelection.RedHerring: return <HerringIcon />;
  }
};

const classes = createStyles({
  select: {
    WebkitAppearance: 'none',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
  },
});

export const InventoryModuleComponent: React.FC<ModuleComponentProps<ModuleType.Inventory>> = ({
  module,
  grabbing,
  onGrabStart,
}) => {
  return (
    <>
      <path
        fill="#e6e6e6"
        stroke="#4d4d4d"
        d="M0.5 0.5H301.5V600.5H0.5z"
      />
      <path fill="#1a1a1a" d="M121 20.5H281V180.5H121z" />
      <path
        fill="#333"
        d="M560.31 554.48h-6.24v-6.24h6.24zm38.25 0h-33.51v-6.24h33.51zM598.56 536.92h-33.51v-26.58a7.05 7.05 0 011-3.78 7.93 7.93 0 012.74-2.72 7.23 7.23 0 013.73-1h26v6.24h-25.79a1.43 1.43 0 00-1 .43 1.39 1.39 0 00-.43 1v18.72a1.47 1.47 0 001.44 1.44h25.83zM598.56 475.45l-33.51 18.49v-7.23l26.46-14.38-26.46-14.39v-7.22l33.51 18.49zM597.52 439.83a8.19 8.19 0 01-2.75 2.74 6.92 6.92 0 01-3.72 1h-18.49a7 7 0 01-3.73-1 8.16 8.16 0 01-2.74-2.74 7 7 0 01-1-3.73V417a7.08 7.08 0 011-3.79 7.9 7.9 0 012.74-2.71 7.13 7.13 0 013.73-1h12.36v27.84h6a1.35 1.35 0 001-.43 1.4 1.4 0 00.44-1v-26.4h6.24v26.59a7.07 7.07 0 01-1.08 3.73zm-18.43-2.46v-21.55h-6.36a1.45 1.45 0 00-1 .41 1.29 1.29 0 00-.43 1v18.72a1.47 1.47 0 001.44 1.44zM598.56 398.78h-33.51V372.2a7.05 7.05 0 011-3.78 7.93 7.93 0 012.74-2.72 7.23 7.23 0 013.73-1h26v6.24h-25.79a1.4 1.4 0 00-1 .44 1.35 1.35 0 00-.43 1v18.72a1.47 1.47 0 001.44 1.44h25.83zM597.52 350.22a7.84 7.84 0 01-2.75 2.71 7.08 7.08 0 01-3.72 1h-36.58v-6.24h10.58V334h6.24v13.75h19.58a1.36 1.36 0 001-.44 1.38 1.38 0 00.44-1V334h6.24v12.47a7.17 7.17 0 01-1.03 3.75zM597.52 321a8.22 8.22 0 01-2.75 2.75 7 7 0 01-3.72 1h-18.49a7.07 7.07 0 01-3.73-1 8.19 8.19 0 01-2.74-2.75 6.92 6.92 0 01-1-3.72v-19.03a7 7 0 011-3.78 7.93 7.93 0 012.74-2.72 7.23 7.23 0 013.73-1h18.49a7.16 7.16 0 013.72 1 8 8 0 012.75 2.72 7.11 7.11 0 011 3.78v19.07a7 7 0 01-1 3.68zm-5.2-3.9v-18.68a1.3 1.3 0 00-.44-1 1.4 1.4 0 00-1-.4h-18.15a1.44 1.44 0 00-1 .4 1.29 1.29 0 00-.43 1v18.72a1.39 1.39 0 00.43 1 1.43 1.43 0 001 .43h18.14a1.49 1.49 0 001.45-1.45zM598.56 281h-26a7.07 7.07 0 01-3.73-1 8.19 8.19 0 01-2.74-2.75 7 7 0 01-1-3.73V254.8h6.24v18.55a1.37 1.37 0 00.43 1 1.39 1.39 0 001 .43h25.83zM611.38 241.17h-6.29v-20.51a1.51 1.51 0 00-1.45-1.45h-5.08v20.34a7.22 7.22 0 01-1 3.76 7.84 7.84 0 01-2.75 2.74 7.07 7.07 0 01-3.72 1h-25.93v-6.24h25.71a1.35 1.35 0 001-.43 1.4 1.4 0 00.44-1v-18.72a1.38 1.38 0 00-.44-1 1.36 1.36 0 00-1-.44h-25.71V213h38.71a7.24 7.24 0 013.79 1 7.65 7.65 0 012.71 2.71 7.24 7.24 0 011 3.79z"
        transform="translate(-532 -83.44)"
      />
      <path fill="#000" d="M121 260.5H281V420.5H121z" />
      <path
        fill="#b3b3b3"
        stroke="#4d4d4d"
        d="M121.5 181H200.5V241H121.5z"
      />
      <path
        fill="#b3b3b3"
        stroke="#4d4d4d"
        d="M200.5 181H280.5V241H200.5z"
      />
      <path d="M122 211.5H282V241.5H122z" opacity="0.1" />
      <path
        fill="none"
        stroke="#000"
        strokeWidth="3"
        d="M140.93 198.47L160.97 218.5 181 198.47"
        opacity="0.33"
      />
      <path
        fill="none"
        stroke="#f2f2f2"
        strokeWidth="3"
        d="M140.93 200.47L160.97 220.5 181 200.47"
      />
      <path
        fill="none"
        stroke="#000"
        strokeWidth="3"
        d="M220.93 218.5L240.97 198.47 261 218.5"
        opacity="0.33"
      />
      <path
        fill="none"
        stroke="#f2f2f2"
        strokeWidth="3"
        d="M220.93 220.5L240.97 200.47 261 220.5"
      />
      <path
        fill="none"
        stroke="#000"
        d="M328 378.94H928V388.94H328z"
        opacity="0.2"
        transform="rotate(-90 320.28 608.22)"
      />
      <path
        fill="#333"
        d="M251 560.98L231 550.98 231 570.98 251 560.98z"
      />
      <circle cx="201" cy="560.98" r="19.5" fill="#61BAC9" />
      <path
        fill="#549391"
        d="M733 625.41a19 19 0 11-19 19 19 19 0 0119-19m0-1a20 20 0 1020 20 20 20 0 00-20-20z"
        transform="translate(-532 -83.44)"
      />
      <g fill="#fff" opacity="0.75">
        <circle cx="201" cy="561.98" r="9.5" />
        <path
          d="M733 636.41a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z"
          transform="translate(-532 -83.44)"
        />
      </g>
      <circle cx="201" cy="560.98" r="9.5" />
      <path
        fill="#549391"
        d="M733 635.41a9 9 0 11-9 9 9 9 0 019-9m0-1a10 10 0 1010 10 10 10 0 00-10-10z"
        transform="translate(-532 -83.44)"
      />
      <text
        fill="#333"
        fontSize="27.77"
        transform="translate(172.45 530.76)"
      >
        ITEM
      </text>
      <path
        fill="#333"
        d="M808.26 513.94a4.74 4.74 0 014.74 4.74v40.51a4.75 4.75 0 01-4.74 4.75H657.74a4.75 4.75 0 01-4.74-4.75v-40.51a4.74 4.74 0 014.74-4.74h150.52m0-1H657.74a5.75 5.75 0 00-5.74 5.74v40.51a5.76 5.76 0 005.74 5.75h150.52a5.76 5.76 0 005.74-5.75v-40.51a5.75 5.75 0 00-5.74-5.74z"
        transform="translate(-532 -83.44)"
      />
      <rect width="150" height="40" x="126" y="435.5" rx="2" />
      <rect
        width="150"
        height="40"
        x="126"
        y="433.5"
        fill="#333"
        rx="2"
      />
      <text
        fill="#fff"
        fontSize="21"
        transform="translate(181.75 460.5)"
      >
        SET
      </text>

      <g filter={`url(#${FILTER_GLOW_SUBTLE_ID})`}>
        <path
          fill="none"
          stroke="#e8d310"
          d="M131 30.5H271V170.5H131z"
        />
        <g transform={`translate(0 ${operations.indexOf(module.config.operation) * 40})`}>
          <path fill="#e8d310" d="M131 40.5H271V80.5H131z" />
        </g>

        <path
          fill={module.config.operation === InventoryOperation.Lacks ? '#333' : '#e8d310'}
          d="M684.67 156.17v-23.33H688v20h13.33v3.33zM708 156.17h-3.33v-16.66h1.66v-1.67H708v-1.67h1.67v-1.66h1.66v-1.67h3.34v1.67h1.66v1.66H718v1.67h1.67v1.67h1.66v16.66H718v-6.66h-10zm0-10h10v-5h-1.67v-1.66h-1.66v-1.67h-3.34v1.67h-1.66v1.66H708zM729.67 152.84h6.66v-1.67H738v-1.66h3.33v3.33h-1.66v1.67H738v1.66h-10v-1.66h-1.67v-1.67h-1.66v-16.67h1.66v-1.66H728v-1.67h10v1.67h1.67v1.66h1.66v3.34H738v-1.67h-1.67v-1.67h-6.66v1.67H728v13.33h1.67zM748 146.17v10h-3.33v-23.33H748v10h1.67v-1.67h1.66v-1.66H753v-1.67h1.67v-1.67h1.66v-1.66H758v-1.67h3.33v3.33h-1.66v1.67H758v1.67h-1.67v1.66h-1.66v1.67H753v3.33h1.67v1.67h1.66v1.67H758v1.66h1.67v1.67h1.66v3.33H758v-1.66h-1.67v-1.67h-1.66v-1.67H753v-1.66h-1.67v-1.67h-1.66v-1.67zM779.67 136.17h1.66v3.34H778v-1.67h-1.67v-1.67h-6.66v1.67H768v3.33h1.67v1.67H778v1.67h1.67v1.66h1.66v6.67h-1.66v1.67H778v1.66h-10v-1.66h-1.67v-1.67h-1.66v-3.33H768v1.66h1.67v1.67h6.66v-1.67H778v-3.33h-1.67v-1.67H768v-1.66h-1.67v-1.67h-1.66v-6.67h1.66v-1.66H768v-1.67h10v1.67h1.67z"
          transform="translate(-532 -83.44)"
        />

        <path
          fill={module.config.operation === InventoryOperation.Has ? '#333' : '#e8d310'}
          d="M708 187.13v10h-3.33V173.8H708v10h10v-10h3.33v23.33H718v-10zM728 197.13h-3.33v-16.66h1.66v-1.67H728v-1.67h1.67v-1.66h1.66v-1.67h3.34v1.67h1.66v1.66H738v1.67h1.67v1.67h1.66v16.66H738v-6.66h-10zm0-10h10v-5h-1.67v-1.66h-1.66v-1.67h-3.34v1.67h-1.66v1.66H728zM759.67 177.13h1.66v3.34H758v-1.67h-1.67v-1.67h-6.66v1.67H748v3.33h1.67v1.67H758v1.67h1.67v1.66h1.66v6.67h-1.66v1.67H758v1.66h-10v-1.66h-1.67v-1.67h-1.66v-3.33H748v1.66h1.67v1.67h6.66v-1.67H758v-3.33h-1.67v-1.67H748v-1.66h-1.67v-1.67h-1.66v-6.67h1.66v-1.66H748v-1.67h10v1.67h1.67z"
          transform="translate(-532 -83.44)"
        />
      </g>
      <path d="M121 20.5H281V180.5H121z" fill={`url(#${PATTERN_LINES_ID})`} />

      {module.config.selection && (
        <g transform="translate(200 340)" filter={`url(#${FILTER_GLOW_ID})`}>
          {renderItem(module.config.selection)}
        </g>
      )}
      <path d="M121 260.5H281V420.5H121z" fill={`url(#${PATTERN_LINES_ID})`} />

      <rect
        className={clsx(moduleStyles.grab, { [moduleStyles.grabbing]: grabbing })}
        width={300}
        height={600}
        onMouseDown={onGrabStart}
      />

      <rect
        className={moduleStyles.hitBox}
        x={120}
        y={180}
        width={80}
        height={60}
        onClick={() => advanceOperation(module, 1)}
      />
      <rect
        className={moduleStyles.hitBox}
        x={200}
        y={180}
        width={80}
        height={60}
        onClick={() => advanceOperation(module, -1)}
      />

      <foreignObject x="126" y="433" width="150" height="40" opacity="0">
        <select
          value={module.config.selection || ''}
          className={classes.select}
          onChange={e => {
            // TODO: The blur() prevents key presses like WASD from changing the selection.
            // This `select` will eventually be replaced by something like a modal
            // since there'll theoretically be LOTS of things to choose from.
            e.currentTarget.blur();
            module.config.selection = e.currentTarget.value as InventorySelection || null;
          }}
        >
          <option value="">
            <Message text="" />
          </option>
          {Object.values(InventorySelection).map(choice => (
            <option key={choice} value={choice}>
              <Message text={messages[choice]} />
            </option>
          ))}
        </select>
      </foreignObject>
    </>
  );
};
